import React, { MouseEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpFromBracket,
  faCompass,
  faFileChartColumn,
  faFolderOpen,
  faGrid,
  faLayerGroup,
  faMagnifyingGlass,
  faSidebar,
  faTable,
} from '@fortawesome/pro-solid-svg-icons';
import { faDrawPolygon } from '@fortawesome/pro-regular-svg-icons';

import Dropdown from '@mysidewalk/react-dropdown';
import { MYSIDEWALK_LOGO_MARK_SVG } from './BrandUtils';
import {
  CONVERSATIONS_NEW,
  DASHBOARDS_INDEX,
  FOLDERS_INDEX,
  LAYERS_INDEX,
  LAYERS_NEW_DRAW,
  LAYERS_NEW_UPLOAD,
  REPORTS_INDEX,
  WORKSPACES_INDEX,
  WORKSPACES_NEW_DATA_GUIDES,
  WORKSPACES_NEW_DATA_SEARCH,
} from './URLs';

import './NavigationMenu.css';
import Access from './types/Access';
import Domains from './types/Domains';
import LinkHandler from './types/LinkHandler';

export type Props = {
  access: Access;
  domains: Domains;
  onClickLink: LinkHandler;
  productName?: string;
  productLogo?: string;
  links?: Record<string, string>;
};

function NavigationMenu({
  access,
  domains,
  onClickLink,
  productName = '',
  productLogo = MYSIDEWALK_LOGO_MARK_SVG,
  links = {},
}: Props) {
  const items = [
    {
      id: 'root',
      'data-test-id': 'header.navigation-menu.links.root',
      location: domains.chart,
      label: 'Home',
      isSecondary: false,
    },
    {
      id: 'sidekick',
      'data-test-id': 'header.navigation-menu.links.sidekick',
      location: `${domains.sidekick}${CONVERSATIONS_NEW}`,
      label: 'Sidekick',
      isSecondary: false,
      isUnavailable: !access.hasSidekickAccess,
    },
    {
      id: 'seek',
      'data-test-id': 'header.navigation-menu.links.seek',
      location: domains.seek,
      label: 'Seek',
      isSecondary: false,
      isUnavailable: !access.hasSeekAccess,
    },
    {
      id: 'seek-guides',
      'data-test-id': 'header.navigation-menu.links.guides',
      location: `${domains.seek}${WORKSPACES_NEW_DATA_GUIDES}`,
      label: 'Guides',
      icon: faCompass,
      isSecondary: true,
      isUnavailable: !access.hasSeekAccess,
    },
    {
      id: 'seek-workspaces',
      'data-test-id': 'header.navigation-menu.links.workspaces',
      location: `${domains.seek}${WORKSPACES_INDEX}`,
      label: 'Workspaces',
      icon: faTable,
      isSecondary: true,
      isUnavailable: !access.hasSeekAccess,
    },
    {
      id: 'publisher',
      'data-test-id': 'header.navigation-menu.links.chart',
      location: domains.chart,
      label: 'Chart',
      isSecondary: false,
      isUnavailable: !access.hasChartAccess,
    },
    {
      id: 'publisher-reports',
      'data-test-id': 'header.navigation-menu.links.reports',
      location: `${domains.chart}${REPORTS_INDEX}`,
      label: 'Reports',
      icon: faFileChartColumn,
      isSecondary: true,
      isUnavailable: !access.hasChartAccess,
    },
    {
      id: 'publisher-dashboards',
      'data-test-id': 'header.navigation-menu.links.dashboards',
      location: `${domains.chart}${DASHBOARDS_INDEX}`,
      label: 'Dashboards',
      icon: faSidebar,
      isSecondary: true,
      isUnavailable: !access.hasChartAccess,
    },
    {
      id: 'publisher-projects',
      'data-test-id': 'header.navigation-menu.links.folders',
      location: `${domains.chart}${FOLDERS_INDEX}`,
      label: 'Folders',
      icon: faFolderOpen,
      isSecondary: true,
      isUnavailable: !access.hasChartAccess,
    },
    {
      id: 'data',
      'data-test-id': 'header.navigation-menu.links.customer-data',
      location: `${domains.chart}${LAYERS_INDEX}`,
      label: 'Your Team\'s Data',
      isSecondary: false,
      isUnavailable: !access.hasChartAccess,
    },
    {
      id: 'data-layers',
      'data-test-id': 'header.navigation-menu.links.customer-data',
      location: `${domains.chart}${LAYERS_INDEX}`,
      label: 'Layers',
      icon: faLayerGroup,
      isSecondary: true,
      isUnavailable: !access.hasChartAccess,
    },
    {
      id: 'data-upload',
      'data-test-id': 'header.navigation-menu.links.customer-data-upload',
      location: `${domains.chart}${LAYERS_NEW_UPLOAD}`,
      label: 'Upload data',
      icon: faArrowUpFromBracket,
      isSecondary: true,
      isUnavailable: !access.hasChartAccess,
    },
    {
      id: 'data-draw',
      'data-test-id': 'header.navigation-menu.links.customer-data-draw',
      location: `${domains.chart}${LAYERS_NEW_DRAW}`,
      label: 'Draw a boundary',
      icon: faDrawPolygon,
      isSecondary: true,
      isUnavailable: !access.hasChartAccess,
    },
  ];

  // Update the location of each item if it's in the links object
  items.forEach((item) => {
    if (item.id in links) {
      item.location = links[item.id];
    }
  });

  const navigation = (
    access.hasAuthenticatedAccess
      ? (
        <Dropdown variant="dark" className="ApplicationHeader-Dropdown">
          <Dropdown.Toggle
            className="ApplicationHeader-action ApplicationHeader-action--emphasis"
            data-test-id="header.navigation-menu.toggle"
            title="View navigation options"
          >
            <FontAwesomeIcon
              className="fa-xl"
              icon={faGrid}
            />
            <span className="sr-only">Navigation</span>
          </Dropdown.Toggle>
          <Dropdown.Menu width="sm">
            <Dropdown.Item
              id="application-menu-catalog-search"
              data-test-id="header.navigation-menu.links.search"
              style={{ padding: 0 }}
              href={`${domains.seek}${WORKSPACES_NEW_DATA_SEARCH}`}
              onClick={onClickLink}
            >
              <span
                style={{
                  background: 'var(--mysw-white)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flex: '1',
                  maxWidth: '100%',
                  padding: 'var(--mysw-spacer-1) var(--mysw-spacer-2)',
                  margin: 'var(--mysw-spacer-1) var(--mysw-spacer-2)',
                  borderRadius: 'var(--mysw-rounded-1)',
                  color: 'var(--mysw-gray-dark)',
                }}
              >
                <FontAwesomeIcon className="fa-fw" icon={faMagnifyingGlass} />
                Search data
              </span>
            </Dropdown.Item>
            {items.map((item) => {
              const onClickItem = (event: React.MouseEvent, close?: () => void) => {
                if (!item.isUnavailable) onClickLink(event, close);
              };

              if (item.isUnavailable) return null;

              return (
                <Dropdown.Item
                  key={item.id}
                  disabled={item.isUnavailable}
                  id={`application-menu-${item.id}`}
                  style={{
                    paddingLeft: item.isSecondary ? 'var(--mysw-spacer-4)' : undefined,
                    fontWeight: !item.isSecondary ? 'var(--mysw-weight-bold)' : undefined,
                    fontSize: item.isSecondary ? 'var(--mysw-size-700)' : undefined,
                  }}
                  className={item.isSecondary ? 'Dropdown-Item_secondary' : undefined}
                  data-test-id={item['data-test-id']}
                  href={item.isUnavailable ? undefined : item.location}
                  title={item.isUnavailable ? 'You don\'t have access to this' : undefined}
                  onClick={onClickItem}
                >
                  {
                    item.icon
                      ? <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={item.icon} /></Dropdown.ItemIcon>
                      : null
                  }
                  {item.label}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      )
      : null
  );

  return (
    <div className="Navigation">
      {navigation}
      <a
        href={domains.root}
        className="Navigation-product"
        data-test-id="header.product.links.root"
        onClick={onClickLink}
      >
        <img
          src={productLogo}
          className="Navigation-productBrand"
          data-test-id="header.product.brand"
          alt=""
        />
        <span
          className="Navigation-productName"
          data-test-id="header.product.name"
        >
          {productName}
        </span>
      </a>
    </div>
  );
}

NavigationMenu.displayName = 'NavigationMenu';

export default NavigationMenu;
